import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import axios from 'axios';

import {
  TextInput,
  I18n,
  Link,
  Icon,
  Button,
  Status,
  UpdatingStatus,
} from 'components';

import { getRandomPassword, getNewPasswordHint, parseRegExpToGetLengthValues } from './utils';
import { SAME_PASSWORD_TEXT } from '../../constants';

import styles from './styles.pcss';
const cx = classNames.bind(styles);

class ChangePassword extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      newPassword: '',
      newPasswordHint: null,
      suggestedPassword: getRandomPassword({
        regexp: this.props.regexp,
        userLogin: this.props.userLogin,
        userInternetLogin: this.props.userInternetLogin,
      }),
      ipoe: {},
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentPassword !== prevProps.currentPassword) {
      this.updateNewPassword(this.state.newPassword);
    }
  }

  setSuggestedPassword = () => this.updateNewPassword(this.state.suggestedPassword);

  generateNewSuggestedPassword = () => {
    const { regexp, userLogin, userInternetLogin } = this.props;

    this.setState({
      suggestedPassword: getRandomPassword({
        regexp,
        userLogin,
        userInternetLogin,
      }),
    });
  };

  updateNewPassword = (value) => {
    const { regexp, userLogin, userInternetLogin, currentPassword } = this.props;

    this.setState({
      newPassword: value,
      newPasswordHint: getNewPasswordHint({
        value,
        regexp,
        userLogin,
        currentPassword,
        userInternetLogin,
      }),
    });
  };

  submitPassword = () => {
    this.setState({
      newPassword: '',
      newPasswordHint: null,
    });
    axios.get('/api/profile/homeinternet/ipoe/')
      .then(({ data }) => this.setState({ ipoe: data }))
      .catch((error) => error);
    this.props.onSubmitPassword(this.state.newPassword);
  };

  render() {
    const {
      passwordUpdatingStatus, isFttb, isConvergent, rulesHint, regexp,
    } = this.props;
    const {
      newPassword,
      newPasswordHint,
      suggestedPassword,
      ipoe,
    } = this.state;

    return (
      <>
        <div className={cx('container')}>
          <div className={cx('label')}>
            <I18n path="settings.passwordAndAccess.newPassword" />
          </div>
          <div className={cx('inputWrapper')}>
            <TextInput
              type="password"
              value={newPassword}
              maxLength={parseRegExpToGetLengthValues(regexp).max}
              onChange={this.updateNewPassword}
            />
            {newPasswordHint !== 'good' && (
              <span
                className={cx('rulesHint')}
                dangerouslySetInnerHTML={{
                  __html: newPasswordHint === 'same'
                    ? SAME_PASSWORD_TEXT
                    : rulesHint,
                }}
              />
            )}
          </div>
          <div className={cx('hintWrapper')}>
            {newPasswordHint && (
              <I18n
                className={cx('hint', newPasswordHint)}
                path={`settings.passwordAndAccess.reliability.${newPasswordHint}`}
              />
            )}
          </div>
        </div>

        <div className={cx('container')}>
          <div className={cx('label', 'hiddenOnPhones')} />
          <div className={cx('inputWrapper', 'suggestedPasswordWrapper')}>
            <I18n path="settings.passwordAndAccess.example" />
            :&nbsp;
            <Link
              className={cx('suggestedPassword')}
              onClick={this.setSuggestedPassword}
            >
              {suggestedPassword}
            </Link>
            <div
              role="presentation"
              onClick={this.generateNewSuggestedPassword}
              className={cx('arrowCircled')}
            >
              <Icon name="arrow-circled" />
            </div>
          </div>
        </div>

        <UpdatingStatus
          className={cx('status')}
          {...passwordUpdatingStatus}
          successMessage="settings.passwordAndAccess.passwordIsChanged"
          errorMessage={isFttb
            ? 'common.operationFailed'
            : 'settings.passwordAndAccess.wrongCurrentPasswordError'}
        />

        {passwordUpdatingStatus.success && isFttb && (
          <Status
            className={['attention', cx('status')]}
            emoji="statusFail"
          >
            {ipoe.ipoeUsed && (
              <I18n path="ipoe.password.captive" />
            )}

            {ipoe.realEquipment && !ipoe.ipoeUsed && (
              <I18n path="ipoe.password.autoupdate" />
            )}

            {!ipoe.realEquipment && !ipoe.ipoeUsed && (
              <I18n path="ipoe.password.needUpdate" />
            )}
          </Status>
        )}

        <Button
          disabled={
            this.props.disabled
            || passwordUpdatingStatus && passwordUpdatingStatus.isUpdating
            || !newPassword
            || newPasswordHint !== 'good'
          }
          onClick={this.submitPassword}
        >
          <I18n path="settings.passwordAndAccess.changePassword" />
        </Button>
      </>
    );
  }
}

ChangePassword.propTypes = {
  onSubmitPassword: PropTypes.func,
  passwordUpdatingStatus: PropTypes.object,
  disabled: PropTypes.bool,
  isFttb: PropTypes.bool,
  isConvergent: PropTypes.bool,
  userLogin: PropTypes.string,
  userInternetLogin: PropTypes.string,
  rulesHint: PropTypes.string,
  regexp: PropTypes.string,
  currentPassword: PropTypes.string,
};

export default ChangePassword;
